import React, { forwardRef } from 'react'
import clsx from 'clsx'
import uniqueId from 'lodash.uniqueid'
import { NumericInput as NumericInputStyles } from '@airbus/elements/elements'
import { Add, Remove } from '@mui/icons-material'
import useControlled from '../../lib/hooks/useControlled'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { createUseStyles } from '../../lib/styling'
import { countDecimals, precisionRound } from '../../lib/utils'
import IconButton from '../IconButton'
import { CommonProps } from '../helperTypes'

type Props = {
  /** general label */
  ariaLabel?: string
  /**
   * aria-label for decrement button
   * @default "Decrease value"
   */
  ariaLabelDown?: string
  /**
   * aria-label for increment button
   * @default "Increase value"
   */
  ariaLabelUp?: string
  /**
   * default value when uncontrolled
   * @default min value
   */
  defaultValue?: number
  /** disable the whole component */
  disabled?: boolean
  /**
   * The id that will be passed to the underlying input element.
   */
  id?: string
  /**
   * Enable invalid/error visual status
   * @default false
   **/
  invalid?: boolean
  /** If enabled the increment and decrement buttons will loop indefinitely */
  loop?: boolean
  /**
   * Minimum value
   * @default 0
   */
  min?: number
  /**
   * Maximum value for counter
   * @default 100
   */
  max?: number
  /**
   * Callback when value changes.<br/ >
   * Will only get called on blur with the parsed value (invalid values will be clamped between min/max)
   */
  onChange?: (value: number | undefined) => void
  /** content will be passed as placeholder */
  placeholder?: string
  /**
   * Size of the numeric input
   * @default medium
   */
  size?: 'small' | 'medium'
  /**
   * Value to be added or subtracted on each step the slider makes.<br />
   * Must be greater than zero.<br />
   * `max` and `min` should be evenly divisible by the step value.
   * @default 1
   */
  step?: number
  /**
   * Enable valid/success visual status
   * @default false
   **/
  valid?: boolean
  /** current value when [controlled](https://react.dev/learn/sharing-state-between-components#controlled-and-uncontrolled-components) */
  value?: number
}

type DeprecatedProps = {
  labelDown?: string
  labelInput?: string
  labelUp?: string
  minValue?: number
  maxValue?: number
  rollup?: boolean
}

export type NumericInputProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: NumericInputProps) => {
  const {
    ariaLabel,
    ariaLabelDown,
    ariaLabelUp,
    labelDown,
    labelInput,
    labelUp,
    loop,
    max,
    maxValue,
    min,
    minValue,
    rollup,
    ...rest
  } = props
  return {
    ariaLabel: labelInput || ariaLabel,
    ariaLabelDown: labelDown || ariaLabelDown,
    ariaLabelUp: labelUp || ariaLabelUp,
    loop: rollup || loop,
    max: maxValue || max,
    min: minValue || min,
    ...rest,
  }
}

const checkProps = (
  { defaultValue, min = 0, max = 100, step = 1, value }: NumericInputProps,
  precision: number,
): void => {
  if (process.env.NODE_ENV === 'production') return
  const isInRange = (val: number) => val >= min && val <= max
  if (defaultValue && !isInRange(defaultValue)) {
    console.warn(
      [
        '[AIRBUS-DS] The NumericInput expects the `defaultValue` prop to be contained in `min` and `max` range.',
        `However, [${defaultValue}] was provided.`,
      ].join('\n'),
    )
  }
  if (value && !isInRange(value)) {
    console.warn(
      [
        '[AIRBUS-DS] The NumericInput expects the `value` prop to be contained in `min` and `max` range.',
        `However, [${value}] was provided.`,
      ].join('\n'),
    )
  }
  if (step < 0) {
    console.warn(
      [
        '[AIRBUS-DS] The NumericInput expects the `step` prop to be greater than zero.',
        `However, "${step}" was provided.`,
      ].join('\n'),
    )
  }
  const factor = Math.pow(10, precision)
  if (((max - min) * factor) % (step * factor) !== 0) {
    console.warn(
      [
        '[AIRBUS-DS] The NumericInput expects the `min` and `max` range to be evenly divisible by the `step` prop.',
        `However, "${step}" was provided.`,
      ].join('\n'),
    )
  }
}

const useStyles = createUseStyles(NumericInputStyles, 'numericinput')

const parseValue = (value: number | string, precision = 2) => {
  let numberValue = typeof value === 'number' ? value : parseFloat(value.replace(',', '.'))
  return precisionRound(numberValue, precision)
}

export const NumericInput = (props: NumericInputProps, ref: React.Ref<HTMLInputElement>) => {
  const {
    ariaLabel,
    ariaLabelDown = 'Decrease number',
    ariaLabelUp = 'Increase number',
    className,
    defaultValue,
    disabled,
    id: idProp,
    invalid,
    loop,
    max = 100,
    min = 0,
    onChange,
    size = 'medium',
    step = 1,
    style,
    valid,
    value: valueProp,
    placeholder,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps(
    'NumericInput',
    {
      labelDown: 'ariaLabelDown',
      labelInput: 'ariaLabel',
      labelUp: 'ariaLabelUp',
      minValue: 'min',
      maxValue: 'max',
      rollup: 'loop',
    },
    props,
  )
  const defaultId = React.useRef<string>(uniqueId('NumericInput-'))
  const id = idProp || defaultId.current
  const precision = React.useMemo(() => {
    if (Number.isInteger(step)) return 0
    return countDecimals(step)
  }, [step])
  React.useEffect(() => checkProps(props, precision), [precision, props])
  const classes = useStyles()

  // controlled / uncontrolled value
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue: defaultValue || min,
  })
  const updateValue = React.useCallback(
    (newValue: number | undefined) => {
      setValue(newValue)
      onChange && onChange(newValue)
    },
    [onChange, setValue],
  )

  // event handlers
  const handleInputFocus = React.useCallback(({ target }: React.FocusEvent<HTMLInputElement>) => {
    // <input type="number"> does not allow selectionStart/selectionEnd
    // @see https://stackoverflow.com/a/55803519/3641075
    target.type = 'text'
    target.selectionStart = 0
    target.selectionEnd = target.value.length
    target.type = 'number'
  }, [])
  const handleInputChange = React.useCallback(
    ({ target }: React.ChangeEvent<HTMLInputElement>) => {
      let { value: newValue } = target
      let numericValue = newValue ? parseValue(newValue, precision) : min
      numericValue = Math.min(max, Math.max(min, numericValue))
      updateValue(numericValue)
    },
    [max, min, precision, updateValue],
  )
  const handleKeyDownCapture = (evt: React.KeyboardEvent<HTMLDivElement>) => {
    const managedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown']
    const { key } = evt.nativeEvent
    if (!managedKeys.includes(key)) return
    evt.preventDefault()

    switch (key) {
      default:
      case 'ArrowDown':
      case 'ArrowLeft':
        handleDecrement()
        break
      case 'ArrowUp':
      case 'ArrowRight':
        handleIncrement()
        break
    }
  }

  const handleIncrement = (): void => {
    let newValue = value + step
    if (newValue < min) newValue = min
    if (newValue > max) newValue = loop ? min : max
    updateValue(parseValue(newValue, precision))
  }

  const handleDecrement = (): void => {
    let newValue = value - step
    if (newValue > max) newValue = max
    if (newValue < min) newValue = loop ? max : min
    updateValue(parseValue(newValue, precision))
  }

  return (
    <div
      aria-label={ariaLabel}
      className={clsx(classes.root, className, {
        [classes._disabled]: disabled,
        [classes._invalid]: invalid,
        [classes._valid]: valid,
        [classes._small]: size === 'small',
      })}
      style={style}
      id={id}
      onKeyDownCapture={handleKeyDownCapture}
    >
      <IconButton aria-label={ariaLabelDown} className={classes.button} onClick={handleDecrement} tabIndex={-1}>
        <Remove />
      </IconButton>
      <input
        aria-labelledby={ariaLabel && id}
        className={classes.input}
        ref={ref}
        min={min}
        max={max}
        onChange={handleInputChange}
        onFocus={handleInputFocus}
        placeholder={`${placeholder ?? min}`}
        step={step}
        type='number'
        value={value}
        {...rest}
      ></input>
      <IconButton aria-label={ariaLabelUp} className={classes.button} tabIndex={-1} onClick={handleIncrement}>
        <Add />
      </IconButton>
    </div>
  )
}

export default forwardRef<HTMLInputElement, NumericInputProps>(NumericInput)
